import { useEffect, useState } from 'react'
import {
  Box,
  Stack,
  Typography,
  Button,
  Paper,
  Grow,
  Fade,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd'
import SchoolIcon from '@mui/icons-material/School'
import SportsEsportsIcon from '@mui/icons-material/SportsEsports'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

interface MemorizationOnboardingProps {
  /** Where the primary CTA goes when clicked. Defaults to /reader. */
  primaryCtaPath?: string
  /** Optional secondary action shown below the primary CTA (e.g. "Back to Games"). */
  secondaryAction?: { labelKey: string; onClick: () => void }
  /** Optional title override (i18n key). Defaults to onboarding.memorization.title. */
  titleKey?: string
  /** Optional subtitle override (i18n key). */
  subtitleKey?: string
  /**
   * When provided, renders a small "Don't show again" link that calls this
   * handler. Caller is responsible for persisting the dismiss (e.g. PUT
   * /profile with `onboardingCompleted: { memorization: true }`).
   */
  onDismiss?: () => void
}

const STEP_DELAY_MS = 140

export function MemorizationOnboarding({
  primaryCtaPath = '/reader',
  secondaryAction,
  titleKey = 'onboarding.memorization.title',
  subtitleKey = 'onboarding.memorization.subtitle',
  onDismiss,
}: MemorizationOnboardingProps) {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  // For reduced-motion users we skip the staged paint-then-animate dance —
  // mounted starts true so Grow/Fade render in their final state immediately.
  const [mounted, setMounted] = useState(prefersReducedMotion)

  // Mount on next paint so the Grow/Fade transitions actually run. When the
  // user prefers reduced motion we bypass this entirely (mounted is already
  // true), so the cards just render in place.
  useEffect(() => {
    if (prefersReducedMotion) {
      setMounted(true)
      return
    }
    const r = requestAnimationFrame(() => setMounted(true))
    return () => cancelAnimationFrame(r)
  }, [prefersReducedMotion])

  // Render the supplied i18n key only when it actually exists in the bundle;
  // otherwise fall through to a sensible English default. i18next would
  // normally fall back to EN automatically — this is the last line of defense
  // for the case where an EN key has been removed by mistake, so we never
  // render the raw key (e.g. "onboarding.memorization.title") to a user.
  const safeT = (key: string, fallback: string) =>
    i18n.exists(key) ? t(key) : fallback

  // primaryCtaPath is intentionally an internal app path (e.g. "/reader").
  // We guard against absolute / external URLs below — react-router-dom's
  // navigate() does not protect against this.
  const safePrimaryPath = primaryCtaPath.startsWith('/') && !primaryCtaPath.startsWith('//')
    ? primaryCtaPath
    : '/reader'

  const stepDelay = prefersReducedMotion ? 0 : STEP_DELAY_MS

  const steps: Array<{ icon: React.ReactNode; titleKey: string; descKey: string }> = [
    {
      icon: <MenuBookIcon sx={{ fontSize: 28 }} />,
      titleKey: 'onboarding.memorization.step1Title',
      descKey: 'onboarding.memorization.step1Desc',
    },
    {
      icon: <BookmarkAddIcon sx={{ fontSize: 28 }} />,
      titleKey: 'onboarding.memorization.step2Title',
      descKey: 'onboarding.memorization.step2Desc',
    },
    {
      icon: <SchoolIcon sx={{ fontSize: 28 }} />,
      titleKey: 'onboarding.memorization.step3Title',
      descKey: 'onboarding.memorization.step3Desc',
    },
    {
      icon: <SportsEsportsIcon sx={{ fontSize: 28 }} />,
      titleKey: 'onboarding.memorization.step4Title',
      descKey: 'onboarding.memorization.step4Desc',
    },
  ]

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: { xs: 2, md: 4 }, px: 1 }}>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 720,
          p: { xs: 3, sm: 5 },
          borderRadius: 4,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          textAlign: 'center',
          // Soft warm gradient backdrop reinforces the parchment aesthetic
          background: (t) =>
            t.palette.mode === 'dark'
              ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.04)} 0%, ${t.palette.background.paper} 60%)`
              : `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.04)} 0%, ${t.palette.background.paper} 60%)`,
        }}
      >
        {/* Header */}
        <Fade in={mounted} timeout={500}>
          <Stack spacing={1.5} sx={{ alignItems: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'primary.contrastText',
                bgcolor: 'primary.main',
                boxShadow: (t) =>
                  t.palette.mode === 'dark'
                    ? '0 6px 20px rgba(0,0,0,0.45)'
                    : '0 6px 18px rgba(15,118,110,0.30)',
                animation: 'gentlePulse 2.6s ease-in-out infinite',
                '@keyframes gentlePulse': {
                  '0%, 100%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.06)' },
                },
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none',
                },
              }}
            >
              <SchoolIcon sx={{ fontSize: 34 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
              {safeT(titleKey, "Let's get your memorization journey started")}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                maxWidth: 480,
                lineHeight: 1.6,
                // FR translations are noticeably longer than EN; let long
                // unbroken strings wrap inside the 720px container instead of
                // forcing horizontal scroll on narrow viewports.
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              {safeT(subtitleKey, 'A few quick steps and you are ready to play and review verses.')}
            </Typography>
          </Stack>
        </Fade>

        {/* Steps */}
        <Stack spacing={1.5} sx={{ mb: 4 }}>
          {steps.map((step, idx) => (
            <Grow
              key={step.titleKey}
              in={mounted}
              timeout={400}
              style={{ transformOrigin: 'left center', transitionDelay: `${idx * stepDelay}ms` }}
            >
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  alignItems: 'center',
                  textAlign: 'left',
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.06 : 0.04),
                  border: 1,
                  borderColor: alpha(theme.palette.primary.main, 0.12),
                  transition: 'transform 0.2s ease, border-color 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    borderColor: alpha(theme.palette.primary.main, 0.32),
                  },
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    flexShrink: 0,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    border: 1,
                    borderColor: 'divider',
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {step.icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: 'primary.main',
                        letterSpacing: '0.08em',
                      }}
                    >
                      {String(idx + 1).padStart(2, '0')}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {t(step.titleKey)}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.25,
                      // Long French step descriptions can run wider than the
                      // step box; allow them to wrap rather than overflow.
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word',
                    }}
                  >
                    {t(step.descKey)}
                  </Typography>
                </Box>
              </Stack>
            </Grow>
          ))}
        </Stack>

        {/* CTAs */}
        <Fade in={mounted} timeout={700} style={{ transitionDelay: `${steps.length * stepDelay}ms` }}>
          <Stack spacing={1.5} sx={{ alignItems: 'center' }}>
            <Button
              size="large"
              variant="contained"
              onClick={() => navigate(safePrimaryPath)}
              endIcon={
                <ArrowForwardIcon
                  sx={{
                    transition: 'transform 0.2s ease',
                  }}
                />
              }
              sx={{
                px: 4,
                py: 1.4,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 999,
                // Ensure the primary CTA sits above the secondary on touch
                // devices regardless of source order or future container
                // positioning.
                position: 'relative',
                zIndex: 1,
                boxShadow: (t) =>
                  t.palette.mode === 'dark'
                    ? '0 6px 18px rgba(0,0,0,0.4)'
                    : '0 6px 18px rgba(15,118,110,0.25)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: (t) =>
                    t.palette.mode === 'dark'
                      ? '0 10px 24px rgba(0,0,0,0.5)'
                      : '0 10px 24px rgba(15,118,110,0.35)',
                },
                '&:hover .MuiSvgIcon-root': {
                  transform: 'translateX(4px)',
                },
              }}
            >
              {safeT('onboarding.memorization.primaryCta', 'Open the Quran Reader')}
            </Button>
            {secondaryAction && (
              <Button
                size="small"
                onClick={secondaryAction.onClick}
                // Explicit zIndex keeps the secondary button below the primary
                // CTA's hover lift on touch devices, where the absence of a
                // hover state can briefly leave them at the same z-axis.
                sx={{ color: 'text.secondary', position: 'relative', zIndex: 0 }}
              >
                {t(secondaryAction.labelKey)}
              </Button>
            )}
            {onDismiss && (
              <Button
                size="small"
                onClick={onDismiss}
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.78rem',
                  fontWeight: 500,
                  textDecoration: 'underline',
                  textUnderlineOffset: 3,
                  position: 'relative',
                  zIndex: 0,
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: 'text.primary',
                  },
                }}
              >
                {safeT('onboarding.memorization.dismiss', "Don't show again")}
              </Button>
            )}
          </Stack>
        </Fade>
      </Paper>
    </Box>
  )
}


